import React, { useState, useRef, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { defaultAssignmentsAPI, usersAPI } from '../../services/api';
import { formatDisplayName } from '../../utils/displayName';
import { useToast } from '../../context/ToastContext';

interface DefaultAssignmentsModalProps {
  slotId: string;
  slotTitle: string;
  slotCapacity: number;
  onClose: () => void;
}

const DefaultAssignmentsModal: React.FC<DefaultAssignmentsModalProps> = ({
  slotId,
  slotTitle,
  slotCapacity,
  onClose,
}) => {
  const { t } = useTranslation();
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [filterText, setFilterText] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  useEscapeKey(onClose);

  const { data: defaultAssignments = [], isLoading: loadingAssignments } = useQuery({
    queryKey: ['defaultAssignments', slotId],
    queryFn: () => defaultAssignmentsAPI.getSlotDefaultAssignments(slotId),
  });

  const { data: users = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: usersAPI.getUsers,
  });

  const createMutation = useMutation({
    mutationFn: ({ userId, trainingSlotId }: { userId: string; trainingSlotId: string }) =>
      defaultAssignmentsAPI.createDefaultAssignment(userId, trainingSlotId),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['defaultAssignments'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      setSelectedUserId('');
      setFilterText('');
      toast.success('Default assignment added successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to add default assignment');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (assignmentId: string) =>
      defaultAssignmentsAPI.deleteDefaultAssignment(assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['defaultAssignments'] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      toast.success('Default assignment removed successfully!');
    },
  });

  const handleAdd = () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    // Check if assignment already exists
    const exists = defaultAssignments.some((da) => da.userId === selectedUserId);
    if (exists) {
      toast.error('This user is already assigned as default');
      return;
    }

    // Note: We don't check capacity here at the pattern level
    // The backend will check capacity per instance and create bookings only where capacity allows
    // This allows default assignments even when some dates have regular bookings

    createMutation.mutate({ userId: selectedUserId, trainingSlotId: slotId });
  };

  // Filter out users that already have default assignments
  const availableUsers = users.filter(
    (user) => !defaultAssignments.some((da) => da.userId === user.id)
  );

  const filteredUsers = useMemo(() => {
    if (!filterText.trim()) return availableUsers;
    const q = filterText.toLowerCase();
    return availableUsers.filter((user) => {
      const display = formatDisplayName(user).toLowerCase();
      const email = (user.email || '').toLowerCase();
      return display.includes(q) || email.includes(q);
    });
  }, [availableUsers, filterText]);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectUser = (userId: string) => {
    setSelectedUserId(userId);
    const user = availableUsers.find((u) => u.id === userId);
    setFilterText(user ? formatDisplayName(user) : '');
    setDropdownOpen(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-gray-800">
              {t('admin.defaultAssignmentsFor', { title: slotTitle })}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          <p className="text-gray-600 mb-4">
            {t('admin.defaultAssignmentsDesc')}
          </p>
          
          <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">{t('admin.eventCapacity')}</span> {slotCapacity} |
              <span className="font-semibold ml-2">{t('admin.currentDefaults')}</span> {defaultAssignments.length}
            </p>
          </div>

          {/* Add Default Assignment */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold mb-3">{t('admin.addDefaultAssignment')}</h3>
            <div className="flex gap-2">
              <div className="flex-1 relative" ref={dropdownRef}>
                <input
                  ref={inputRef}
                  type="text"
                  value={filterText}
                  onChange={(e) => {
                    setFilterText(e.target.value);
                    setSelectedUserId('');
                    setDropdownOpen(true);
                  }}
                  onFocus={() => setDropdownOpen(true)}
                  placeholder={
                    loadingUsers
                      ? t('admin.loading')
                      : availableUsers.length === 0
                      ? t('admin.noMatchingUsers')
                      : t('admin.searchUserPlaceholder')
                  }
                  disabled={loadingUsers || availableUsers.length === 0}
                  className="w-full border rounded px-3 py-2"
                />
                {dropdownOpen && filteredUsers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredUsers.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => selectUser(user.id)}
                        className="w-full text-left px-3 py-2 hover:bg-primary-50 text-sm flex justify-between items-center"
                      >
                        <span>{formatDisplayName(user)}</span>
                        <span className="text-xs text-gray-400 ml-2">{user.email}</span>
                      </button>
                    ))}
                  </div>
                )}
                {dropdownOpen && filterText && filteredUsers.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg px-3 py-2 text-sm text-gray-500">
                    {t('admin.noMatchingUsers')}
                  </div>
                )}
              </div>
              <button
                onClick={handleAdd}
                disabled={!selectedUserId || createMutation.isPending}
                className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {createMutation.isPending ? t('admin.adding') : t('admin.add')}
              </button>
            </div>
          </div>

          {/* List of Default Assignments */}
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {t('admin.currentDefaultAssignments')} ({defaultAssignments.length})
            </h3>
            {loadingAssignments ? (
              <p className="text-gray-500">{t('admin.loading')}</p>
            ) : defaultAssignments.length === 0 ? (
              <p className="text-gray-500">{t('admin.noDefaultAssignments')}</p>
            ) : (
              <div className="space-y-2">
                {defaultAssignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="flex justify-between items-center p-3 bg-white border rounded-lg"
                  >
                    <div>
                      <span className="font-medium">{formatDisplayName(assignment.user)}</span>
                      {assignment.user.role === 'ADMIN' && (
                        <span className="ml-2 text-xs bg-gray-200 px-2 py-1 rounded">
                          {t('admin.admin_role')}
                        </span>
                      )}
                      <p className="text-xs text-gray-500">
                        {t('admin.assignedOn')} {format(new Date(assignment.createdAt), 'dd MMM yyyy')}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove default assignment for ${formatDisplayName(assignment.user)}? Existing bookings will remain, but no new bookings will be auto-created.`
                          )
                        ) {
                          deleteMutation.mutate(assignment.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                      className="text-accent-600 hover:text-accent-700 font-medium disabled:opacity-50"
                    >
                      {t('admin.remove')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
            >
              {t('admin.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DefaultAssignmentsModal;

